import type { useIncomingLinksQuery$data } from '../hooks/__generated__/useIncomingLinksQuery.graphql.ts';

import { getIncomingLinkUrlAlias, isCuratedIncomingLinkTypename } from './incomingLinkTypes.ts';

type CypherConnection = NonNullable<useIncomingLinksQuery$data['graphStore_cypherQueryV2']>;
type CypherColumnValue = CypherConnection['edges'][number]['node']['columns'][number]['value'];

export type IncomingLinkNodeData = NonNullable<
	Extract<CypherColumnValue, { __typename: 'GraphStoreCypherQueryV2AriNode' }>['data']
>;

/**
 * The `links` shape the 10 Confluence types return (`ConfluencePageLinks` and
 * friends): `webUi` is a site-relative path and `base` is the site's base url, so
 * neither is navigable alone. Every other catalogued type returns an absolute url
 * string. Both halves are nullable in the schema.
 */
type SiteRelativeLinks = { base?: string | null; webUi?: string | null };

const joinLinks = (links: SiteRelativeLinks | null | undefined): string | undefined => {
	if (!links?.base || !links.webUi) {
		return undefined;
	}
	return `${links.base.replace(/\/+$/, '')}/${links.webUi.replace(/^\/+/, '')}`;
};

/**
 * Reads the navigable URL off an AGG-hydrated graph node, which is the only thing
 * the incoming-links query asks for: everything else on the link comes from
 * resolving that URL through ORS, exactly as outgoing links do.
 *
 * Returns `undefined` when the node's `__typename` is not one of the curated types
 * (see `incomingLinkTypes`) or when the selected field is absent — `url` is
 * nullable on nearly every type in the schema, and a link with no url can't be
 * rendered or navigated.
 */
export const extractIncomingLinkUrl = (
	data: IncomingLinkNodeData | null | undefined,
): string | undefined => {
	const typename = data?.__typename;
	if (!data || !isCuratedIncomingLinkTypename(typename)) {
		return undefined;
	}

	// The alias is built at runtime from the typename, so the generated union type
	// can't narrow the lookup — hence the one cast, over both shapes a curated type
	// can carry.
	const selected = (data as Record<string, string | SiteRelativeLinks | null | undefined>)[
		getIncomingLinkUrlAlias(typename)
	];

	return typeof selected === 'string' ? selected || undefined : joinLinks(selected);
};
